import {addDays, setHours, setMinutes, startOfDay} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormSubmitHandler,
  FormField,
  openConfirmDialog,
} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {DefaultValues} from 'react-hook-form';

import {useGetTireSetDestinationBranchQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {getApiDateTimeString, suffixTestId, TestIdProps} from 'shared/web';

import {
  RequestTireSetFormDefinition,
  RequestTireSetFormDefinitionSchema,
} from '../types/RequestTireSetFormDefinition';
import {PrefillFormData} from './PrefillFormData';

export type RequestTireSetData = {
  requestBranchId: string;
  neededAt: string;
  note?: string | null;
};

interface RequestTireSetFormProps extends TestIdProps {
  tireSetId: string;
  tireOrderId: string;
  defaultValues?: DefaultValues<RequestTireSetFormDefinition>;
  onCancelRequest?: () => Promise<void>;
  onSubmit: (data: RequestTireSetData) => Promise<void>;
  onClose: VoidFunction;
}

export function RequestTireSetForm(props: RequestTireSetFormProps) {
  const {data: tireSetDestinationBranches, isLoading: isTireSetDestinationBranchesLoading} =
    useGetTireSetDestinationBranchQuery({orderId: props.tireOrderId, setId: props.tireSetId});

  const currentUserBranchesOptions = (tireSetDestinationBranches ?? []).map((branch) => ({
    label: branch.branchName,
    value: branch.branchId,
  }));

  const handleSubmit: FormSubmitHandler<RequestTireSetFormDefinition> = async (formData) => {
    const {neededAtDate, neededAtTime, ...rest} = formData;

    const [hours, minutes] = neededAtTime.split(':');

    const neededAtDateWithHours = setHours(neededAtDate, parseInt(hours));
    const neededAtDateTime = setMinutes(neededAtDateWithHours, parseInt(minutes));
    const neededAt = getApiDateTimeString(neededAtDateTime);

    const requestData = {...rest, neededAt};

    await props.onSubmit(requestData);
  };

  const handleCancelRequest = () => {
    openConfirmDialog({
      id: 'tireSetCancelRequestConfirm',
      text: i18n.t('entity.tireSet.labels.cancelTireRequestQuestion'),
      onConfirm: () => props.onCancelRequest?.(),
    });
  };

  // Calculate disabled dates for DatePicker
  const getDisabledDates = (selectedDestinationBranchId: string) => {
    const selectedBranch = tireSetDestinationBranches?.find(
      (branch) => branch.branchId === selectedDestinationBranchId
    );

    if (!selectedBranch) {
      return [];
    }

    const today = startOfDay(new Date());

    // Generate array of disabled dates from today to transitTimeDays (exclusive)
    const disabledDates = [];
    for (let i = 0; i < selectedBranch.transitTimeDays; i++) {
      disabledDates.push(addDays(today, i));
    }

    return disabledDates;
  };

  return (
    <Form<RequestTireSetFormDefinition>
      experimentalZodSchema={RequestTireSetFormDefinitionSchema}
      onSubmit={handleSubmit}
      defaultValues={props.defaultValues}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <PrefillFormData
            control={control}
            formApi={formApi}
            tireSetDestinationBranches={tireSetDestinationBranches}
          />
          <FormField
            control={control}
            label={i18n.t('entity.tiresInventory.labels.branch')}
            name="requestBranchId"
            type="choice"
            options={currentUserBranchesOptions}
            isLoading={isTireSetDestinationBranchesLoading}
            isRequired
            menuInPortal
            data-testid={suffixTestId('requestedBranchId', props)}
          />
          <Grid columns={2}>
            <FormField
              control={control}
              label={i18n.t('entity.warehouse.labels.neededAt')}
              name="neededAtDate"
              type="date"
              isRequired
              disabledDates={getDisabledDates(formApi.watch('requestBranchId'))}
              minDate={startOfDay(new Date())}
              data-testid={suffixTestId('neededAtDate', props)}
            />
            <Box paddingTop={5}>
              <FormField
                control={control}
                name="neededAtTime"
                type="time"
                isRequired
                data-testid={suffixTestId('neededAtTime', props)}
              />
            </Box>
          </Grid>
          <FormField
            control={control}
            label={i18n.t('general.labels.note')}
            name="note"
            type="textarea"
            data-testid={suffixTestId('note', props)}
          />
          <HStack justify={props.onCancelRequest ? 'space-between' : 'flex-end'}>
            <Show when={props.onCancelRequest}>
              <Button
                title={i18n.t('entity.tireSet.actions.cancelRequest')}
                variant="dangerLink"
                leftIcon="navigation/cancel"
                onClick={handleCancelRequest}
                data-testid={suffixTestId('discardButton', props)}
              />
            </Show>
            <ButtonGroup>
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discardButton', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={
                  props.defaultValues
                    ? i18n.t('general.actions.save')
                    : i18n.t('general.actions.confirm')
                }
                data-testid={suffixTestId('saveButton', props)}
              />
            </ButtonGroup>
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
